import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary } from './CommissionOverview';
import { FormRow, overlayStyle, modalStyle } from './CommissionBeneficiaries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const AGREEMENT_TYPES = ['employee_commission', 'external_referral', 'partner', 'reseller', 'consultant', 'affiliate', 'cross_company', 'one_time_deal', 'custom'];
const STATUSES = ['draft', 'active', 'suspended', 'expired', 'terminated', 'archived'];
const CAP_TYPES = ['no_cap', 'per_transaction', 'per_month', 'per_quarter', 'per_year', 'per_agreement', 'per_beneficiary'];

export default function CommissionAgreements() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/commissions/agreements?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    fetch(`${API}/companies/${companyId}/commissions/beneficiaries?per_page=200`, { headers: getAuthHeaders() })
      .then(res => res.ok ? res.json() : { data: [] })
      .then(json => setBeneficiaries(Array.isArray(json.data) ? json.data : []))
      .catch(() => setBeneficiaries([]));
  }, [companyId]);

  const changeStatus = async (id, newStatus, reason) => {
    try {
      await fetch(`${API}/companies/${companyId}/commissions/agreements/${id}/status`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, reason }),
      });
      fetchList(); setDetail(null);
    } catch (e) { console.error(e); }
  };

  const renew = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/agreements/${id}/renew`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Failed');
      fetchList(); setDetail(null);
    } catch (e) { console.error(e); }
  };

  const removeDraft = async (id) => {
    if (!window.confirm('Delete this draft agreement?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/commissions/agreements/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to delete'); return; }
      fetchList();
    } catch (e) { console.error(e); }
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📄 Commission Agreements</h2>
        <button style={btnPrimary} onClick={() => setEditing('new')}>+ New Agreement</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, width: 220 }} />
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, width: 160 }}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Type', 'Beneficiaries', 'Status', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No agreements yet</td></tr>}
              {list.map(a => (
                <tr key={a.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{a.agreement_code} <span style={{ color: '#9ca3af' }}>v{a.version_number}</span></td>
                  <td style={tdStyle}>{a.name}</td>
                  <td style={tdStyle}>{(a.agreement_type || '').replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{Array.isArray(a.beneficiary_links) ? a.beneficiary_links.length : 0}</td>
                  <td style={tdStyle}><Badge value={a.status} /></td>
                  <td style={tdStyle}><button style={btnSecondary} onClick={() => setDetail(a)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <AgreementModal companyId={companyId} agreement={editing === 'new' ? null : editing} beneficiaries={beneficiaries}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}

      {detail && (
        <div style={overlayStyle} onClick={() => setDetail(null)}>
          <div style={{ ...modalStyle, width: 600 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{detail.name} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({detail.agreement_code})</span></h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <Info label="Type" value={(detail.agreement_type || '').replace(/_/g, ' ')} />
              <Info label="Status" value={<Badge value={detail.status} />} />
              <Info label="Effective From" value={detail.effective_from ? detail.effective_from.split('T')[0] : '—'} />
              <Info label="Effective To" value={detail.effective_to ? detail.effective_to.split('T')[0] : '—'} />
              <Info label="Approval Required" value={detail.approval_required ? 'Yes' : 'No'} />
              <Info label="Priority Mode" value={detail.priority_mode} />
            </div>
            <h4>Beneficiaries</h4>
            <div style={{ marginBottom: 16 }}>
              {Array.isArray(detail.beneficiary_links) && detail.beneficiary_links.length > 0 ? (
                <table style={tableStyle}>
                  <thead><tr>{['Name', 'Rate Override', 'Fixed Override', 'Share %'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
                  <tbody>
                    {detail.beneficiary_links.map(l => (
                      <tr key={l.id}>
                        <td style={tdStyle}>{l.beneficiary?.display_name || l.beneficiary?.user?.name || '—'}</td>
                        <td style={tdStyle}>{l.rate_override ?? '—'}</td>
                        <td style={tdStyle}>{l.fixed_amount_override ?? '—'}</td>
                        <td style={tdStyle}>{l.share_percentage ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div style={{ color: '#9ca3af', fontSize: 13 }}>No beneficiaries linked</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={btnSecondary} onClick={() => { setEditing(detail); setDetail(null); }}>Edit</button>
              {detail.status === 'draft' && <button style={btnSecondary} onClick={() => changeStatus(detail.id, 'active')}>Activate</button>}
              {detail.status === 'active' && <button style={btnSecondary} onClick={() => changeStatus(detail.id, 'suspended')}>Suspend</button>}
              {['active', 'suspended'].includes(detail.status) && (
                <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => { const r = window.prompt('Termination reason:'); if (r !== null) changeStatus(detail.id, 'terminated', r); }}>Terminate</button>
              )}
              <button style={btnSecondary} onClick={() => renew(detail.id)}>Renew</button>
              {detail.status === 'draft' && <button style={{ ...btnSecondary, color: '#ef4444' }} onClick={() => { removeDraft(detail.id); setDetail(null); }}>Delete</button>}
              <button style={{ ...btnSecondary, marginLeft: 'auto' }} onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AgreementModal({ companyId, agreement, beneficiaries, onClose, onSaved }) {
  const [form, setForm] = useState(agreement ? { ...agreement } : {
    name: '', agreement_type: 'employee_commission', applicable_default: true,
    holding_period_days: 0, approval_required: true, cap_type: 'no_cap', priority_mode: 'replace', status: 'draft',
    referral_ownership: 'assigned',
  });
  const [benRows, setBenRows] = useState(
    agreement?.beneficiary_links?.length
      ? agreement.beneficiary_links.map(l => ({ beneficiary_id: l.beneficiary_id, rate_override: l.rate_override, fixed_amount_override: l.fixed_amount_override, share_percentage: l.share_percentage }))
      : [{ beneficiary_id: '', rate_override: '', fixed_amount_override: '', share_percentage: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const u = (field, val) => setForm({ ...form, [field]: val });

  const submit = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, beneficiaries: benRows.filter(b => b.beneficiary_id) };
      const url = agreement ? `${API}/companies/${companyId}/commissions/agreements/${agreement.id}` : `${API}/companies/${companyId}/commissions/agreements`;
      const res = await fetch(url, {
        method: agreement ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.message || 'Failed to save'); }
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 660 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{agreement ? 'Edit Agreement' : 'New Commission Agreement'}</h3>
        {err && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Name *"><input value={form.name || ''} onChange={e => u('name', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Agreement Type">
            <select value={form.agreement_type || 'employee_commission'} onChange={e => u('agreement_type', e.target.value)} style={inputStyle}>
              {AGREEMENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          <FormRow label="Effective From"><input type="date" value={(form.effective_from || '').split('T')[0] || ''} onChange={e => u('effective_from', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Effective To"><input type="date" value={(form.effective_to || '').split('T')[0] || ''} onChange={e => u('effective_to', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Holding Period (days)"><input type="number" value={form.holding_period_days || 0} onChange={e => u('holding_period_days', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Priority Mode">
            <select value={form.priority_mode || 'replace'} onChange={e => u('priority_mode', e.target.value)} style={inputStyle}>
              <option value="stack">Stack</option><option value="replace">Replace</option>
            </select>
          </FormRow>
          <FormRow label="Cap Type">
            <select value={form.cap_type || 'no_cap'} onChange={e => u('cap_type', e.target.value)} style={inputStyle}>
              {CAP_TYPES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </FormRow>
          {form.cap_type !== 'no_cap' && (
            <FormRow label="Cap Amount"><input type="number" value={form.cap_amount || ''} onChange={e => u('cap_amount', e.target.value)} style={inputStyle} /></FormRow>
          )}
          <FormRow label="Min Commission"><input type="number" value={form.min_commission || ''} onChange={e => u('min_commission', e.target.value)} style={inputStyle} /></FormRow>
          <FormRow label="Max Commission"><input type="number" value={form.max_commission || ''} onChange={e => u('max_commission', e.target.value)} style={inputStyle} /></FormRow>
        </div>

        {form.agreement_type === 'external_referral' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
            <FormRow label="Referral Ownership">
              <select value={form.referral_ownership || 'assigned'} onChange={e => u('referral_ownership', e.target.value)} style={inputStyle}>
                <option value="first_touch">First Touch</option><option value="last_touch">Last Touch</option><option value="assigned">Assigned</option>
              </select>
            </FormRow>
            <FormRow label="Referral Validity (days)"><input type="number" value={form.referral_validity_days || ''} onChange={e => u('referral_validity_days', e.target.value)} style={inputStyle} /></FormRow>
          </div>
        )}

        <FormRow label="Approval Required for Settlements">
          <input type="checkbox" checked={!!form.approval_required} onChange={e => u('approval_required', e.target.checked)} />
        </FormRow>
        <FormRow label="Applicable by Default">
          <input type="checkbox" checked={!!form.applicable_default} onChange={e => u('applicable_default', e.target.checked)} />
        </FormRow>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Beneficiaries & Terms</div>
          {benRows.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <select value={b.beneficiary_id} onChange={e => { const r = [...benRows]; r[i] = { ...r[i], beneficiary_id: e.target.value }; setBenRows(r); }} style={{ ...inputStyle, flex: 2 }}>
                <option value="">Select beneficiary...</option>
                {beneficiaries.map(be => <option key={be.id} value={be.id}>{be.display_name || be.user?.name}</option>)}
              </select>
              <input type="number" placeholder="Rate %" value={b.rate_override || ''} onChange={e => { const r = [...benRows]; r[i] = { ...r[i], rate_override: e.target.value }; setBenRows(r); }} style={inputStyle} />
              <input type="number" placeholder="Fixed $" value={b.fixed_amount_override || ''} onChange={e => { const r = [...benRows]; r[i] = { ...r[i], fixed_amount_override: e.target.value }; setBenRows(r); }} style={inputStyle} />
              <input type="number" placeholder="Share %" value={b.share_percentage || ''} onChange={e => { const r = [...benRows]; r[i] = { ...r[i], share_percentage: e.target.value }; setBenRows(r); }} style={inputStyle} />
              <button style={btnSecondary} onClick={() => setBenRows(benRows.filter((_, idx) => idx !== i))}>✕</button>
            </div>
          ))}
          <button style={btnSecondary} onClick={() => setBenRows([...benRows, { beneficiary_id: '', rate_override: '', fixed_amount_override: '', share_percentage: '' }])}>+ Add Beneficiary</button>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1a1a2e' }}>{value}</div>
    </div>
  );
}
